import { LoadingSpinner } from '@ais-ui/ui-core';
import {
  Settings,
  useGetSettings,
  useSetMapLegendItems,
} from '@ais-ui/websockets';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Map from 'ol/Map';
import { useSettingsChecker } from './settingsChecker';
import { useReplayContext } from '../Replay/ReplayContext';

interface SettingsProviderProps {
  children: ReactNode;
}

interface SettingsContextType {
  settings: Settings;
  dataUpdatedAt: number | null;
  mapDetails: MapDetails;
  saveMapState: () => void;
  olMapRef: React.MutableRefObject<any>;
}

type MapDetails = {
  width: number | null;
  height: number | null;
  zoom: number | null;
  center: [number, number] | null;
};

const SettingsContext = createContext<SettingsContextType | null>(null);

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const { isPlaying } = useReplayContext();
  const [settings, setSettings] = useState<Settings | null>(null);
  const { hasCheckedSettings } = useSettingsChecker(settings);
  const { data: fetchedSettings, status, dataUpdatedAt } = useGetSettings();

  // ONLY use this when map isn't accessible via openlayers provider
  const olMapRef = useRef<Map | null>(null);
  const [mapDetails, setMapDetails] = useState<MapDetails>({
    width: null,
    height: null,
    zoom: null,
    center: null,
  });

  const [hasUpdatedLegend, setHasUpdatedLegend] = useState(false);

  const { mutate } = useSetMapLegendItems(isPlaying);

  useEffect(() => {
    if (status === 'success' && fetchedSettings) {
      setSettings(fetchedSettings);
    }
  }, [status, fetchedSettings, dataUpdatedAt]);

  // This is a bit of hack. The backend should be able to tell what items are in no show, but
  //  at the moment it doesn't. So we need to tell it what items are in no show.
  useEffect(() => {
    if (settings && !hasUpdatedLegend) {
      mutate({
        mapLegendItems:
          settings['ribbonPlugin.settings']?.ship_color_noshow_sel || [],
        showItem: false,
      });
      setHasUpdatedLegend(true);
    }
  }, [settings, hasUpdatedLegend, mutate]);

  const saveMapState = () => {
    if (!olMapRef.current) {
      return;
    }

    const mapContainer = olMapRef.current.getTargetElement();
    const view = olMapRef.current.getView();

    if (!mapContainer?.clientHeight || !mapContainer?.clientWidth) {
      return;
    }

    setMapDetails({
      width: mapContainer.clientWidth,
      height: mapContainer.clientHeight,
      zoom: view.getZoom() as number,
      center: view.getCenter() as [number, number],
    });
  };

  if (!settings || !hasCheckedSettings) {
    return <LoadingSpinner className="h-screen w-screen" />;
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        dataUpdatedAt,
        mapDetails,
        saveMapState,
        olMapRef,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(
      'useSettingsContext must be used within a SettingsContextProvider'
    );
  }
  return context;
};
